import { useMemo, useState, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon } from "@/components/icons";
import { RowActionsMenu, type RowAction } from "@/components/row-actions-menu";
import { SelectCheckbox } from "@/components/select-checkbox";
import type { ColumnMeta } from "@/components/table-columns-menu";
import { useTableColumnPrefs } from "@/components/use-table-column-prefs";
import {
  ColumnResizeHandle,
  buildResizableGridTemplate,
  resizeHandlerFor,
} from "@/components/column-resize";
import { MatchStatusTag } from "@/components/match-status-tag";
import type { Match } from "../_data/types";
import { formatDate } from "../../_lib/format";
import { CountryTag } from "@/components/country-tag";
import { ScoreChip } from "@/components/score-chip";
import { DEFAULT_DECISION_STATUS } from "@/components/match-decision-panel";

type SortKey =
  | "score"
  | "name"
  | "country"
  | "dob"
  | "type"
  | "source"
  | "firstImported"
  | "lastUpdated"
  | "status"
  | "mediaSource";
type SortDir = "asc" | "desc";

type SortState = { key: SortKey; dir: SortDir };

const DEFAULT_SORT: SortState = { key: "score", dir: "desc" };

export function useResultsSort(): [SortState, (s: SortState) => void] {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  return [sort, setSort];
}

export const RESULTS_COLUMN_IDS = [
  "name",
  "type",
  "dob",
  "country",
  "score",
  "source",
  "firstImported",
  "lastUpdated",
] as const;

export const MONITORING_RESULTS_COLUMN_IDS = [
  "name",
  "type",
  "country",
  "dob",
  "score",
  "source",
  "firstImported",
  "lastUpdated",
  "status",
] as const;

export const SINGLE_SCREENING_RESULTS_COLUMN_IDS = [
  "name",
  "dob",
  "type",
  "country",
  "watchlist",
  "firstImported",
  "lastUpdated",
  "score",
] as const;

export const ADV_MEDIA_RESULTS_COLUMN_IDS = [
  "title",
  "country",
  "publishedDate",
  "mediaSource",
  "score",
] as const;

export const MONITORING_ADV_MEDIA_RESULTS_COLUMN_IDS = [
  ...ADV_MEDIA_RESULTS_COLUMN_IDS,
  "status",
] as const;

export type ResultsColumnId =
  | (typeof RESULTS_COLUMN_IDS)[number]
  | (typeof MONITORING_RESULTS_COLUMN_IDS)[number]
  | (typeof SINGLE_SCREENING_RESULTS_COLUMN_IDS)[number]
  | (typeof ADV_MEDIA_RESULTS_COLUMN_IDS)[number]
  | (typeof MONITORING_ADV_MEDIA_RESULTS_COLUMN_IDS)[number];

type Column = {
  id: ResultsColumnId;
  header: string;
  width: string;
  align?: "left" | "center";
  sortKey?: SortKey;
  cell: (m: Match) => ReactNode;
};

const COLUMNS_BY_ID: Record<ResultsColumnId, Column> = {
  name: {
    id: "name",
    header: "Name",
    width: "minmax(160px,1fr)",
    sortKey: "name",
    cell: (m) => <div className="truncate px-2">{m.name}</div>,
  },
  type: {
    id: "type",
    header: "Type",
    width: "minmax(120px,1fr)",
    sortKey: "type",
    cell: (m) => <div className="truncate px-2">{m.type}</div>,
  },
  country: {
    id: "country",
    header: "Country",
    width: "minmax(140px,1fr)",
    sortKey: "country",
    cell: (m) =>
      m.country ? (
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden px-2">
          {m.country.split(", ").map((country) => (
            <CountryTag key={country} country={country} />
          ))}
        </div>
      ) : (
        <div className="px-2 text-grey-900">—</div>
      ),
  },
  dob: {
    id: "dob",
    header: "DOB",
    width: "107px",
    sortKey: "dob",
    cell: (m) => <div className="px-2">{formatDate(m.dob)}</div>,
  },
  score: {
    id: "score",
    header: "Score",
    width: "88px",
    align: "center",
    sortKey: "score",
    cell: (m) => (
      <div className="flex justify-center px-2">
        <ScoreChip score={m.score} />
      </div>
    ),
  },
  source: {
    id: "source",
    header: "Source",
    width: "minmax(160px,1fr)",
    sortKey: "source",
    cell: (m) => (
      <div className="px-2">
        <SourceChip source={m.source} />
      </div>
    ),
  },
  watchlist: {
    id: "watchlist",
    header: "Watchlist",
    width: "minmax(160px,1fr)",
    sortKey: "source",
    cell: (m) => (
      <div className="px-2">
        <SourceChip source={m.source} />
      </div>
    ),
  },
  firstImported: {
    id: "firstImported",
    header: "First import",
    width: "minmax(120px,1fr)",
    sortKey: "firstImported",
    cell: (m) => <div className="px-2 text-black">{formatDate(m.firstImported)}</div>,
  },
  lastUpdated: {
    id: "lastUpdated",
    header: "Last updated",
    width: "minmax(120px,1fr)",
    sortKey: "lastUpdated",
    cell: (m) => <div className="px-2 text-black">{formatDate(m.lastUpdated)}</div>,
  },
  title: {
    id: "title",
    header: "Title",
    width: "minmax(220px,1fr)",
    sortKey: "name",
    cell: (m) => <div className="truncate px-2">{m.name}</div>,
  },
  publishedDate: {
    id: "publishedDate",
    header: "Published Date",
    width: "minmax(120px,1fr)",
    sortKey: "lastUpdated",
    cell: (m) => <div className="px-2 text-black">{formatDate(m.lastUpdated)}</div>,
  },
  mediaSource: {
    id: "mediaSource",
    header: "Media Source",
    width: "minmax(160px,1fr)",
    sortKey: "mediaSource",
    cell: (m) => <div className="truncate px-2">{m.mediaSource || "—"}</div>,
  },
  status: {
    id: "status",
    header: "Status",
    width: "minmax(160px,1fr)",
    sortKey: "status",
    cell: (m) => (
      <div className="flex items-center px-2">
        <MatchStatusTag status={m.decision?.status ?? DEFAULT_DECISION_STATUS} />
      </div>
    ),
  },
};

const REQUIRED_COLUMNS: ReadonlySet<ResultsColumnId> = new Set<ResultsColumnId>(["name", "title"]);

export const MONITORING_RESULTS_COLUMNS_META: readonly ColumnMeta[] =
  MONITORING_RESULTS_COLUMN_IDS.map((id) => ({
    id,
    label: COLUMNS_BY_ID[id].header,
    required: REQUIRED_COLUMNS.has(id),
  }));

export const SINGLE_SCREENING_RESULTS_COLUMNS_META: readonly ColumnMeta[] =
  SINGLE_SCREENING_RESULTS_COLUMN_IDS.map((id) => ({
    id,
    label: COLUMNS_BY_ID[id].header,
    required: REQUIRED_COLUMNS.has(id),
  }));

export const ADV_MEDIA_RESULTS_COLUMNS_META: readonly ColumnMeta[] =
  ADV_MEDIA_RESULTS_COLUMN_IDS.map((id) => ({
    id,
    label: COLUMNS_BY_ID[id].header,
    required: REQUIRED_COLUMNS.has(id),
  }));

export const MONITORING_ADV_MEDIA_RESULTS_COLUMNS_META: readonly ColumnMeta[] =
  MONITORING_ADV_MEDIA_RESULTS_COLUMN_IDS.map((id) => ({
    id,
    label: COLUMNS_BY_ID[id].header,
    required: REQUIRED_COLUMNS.has(id),
  }));

/** Batch watchlist results share Single Screening's columns, but persist under
 *  their own key so each table keeps independent prefs. */
export function useBatchResultsColumnPrefs() {
  return useTableColumnPrefs<ResultsColumnId>("batch-results", {
    columnIds: SINGLE_SCREENING_RESULTS_COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
}

export function useMonitoringResultsColumnPrefs() {
  return useTableColumnPrefs<ResultsColumnId>("monitoring-results", {
    columnIds: MONITORING_RESULTS_COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
}

export function useSingleScreeningResultsColumnPrefs() {
  return useTableColumnPrefs<ResultsColumnId>("single-screening-results", {
    columnIds: SINGLE_SCREENING_RESULTS_COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
}

export function useSingleScreeningAdvMediaColumnPrefs() {
  return useTableColumnPrefs<ResultsColumnId>("single-screening-adv-media-results", {
    columnIds: ADV_MEDIA_RESULTS_COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
}

export function useMonitoringAdvMediaColumnPrefs() {
  return useTableColumnPrefs<ResultsColumnId>("monitoring-adv-media-results", {
    columnIds: MONITORING_ADV_MEDIA_RESULTS_COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
}

export function useBatchAdvMediaColumnPrefs() {
  return useTableColumnPrefs<ResultsColumnId>("batch-adv-media-results", {
    columnIds: ADV_MEDIA_RESULTS_COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
}

const LOCKED_FIRST: readonly ResultsColumnId[] = ["name", "title"];
const LOCKED_FIRST_SET = new Set<string>(LOCKED_FIRST);

const ACTIONS_COL_WIDTH = "48px";

export type ColumnWidths = Partial<Record<ResultsColumnId, number>>;

function visibleColumns(
  order: readonly ResultsColumnId[],
  hidden: ReadonlySet<ResultsColumnId>,
): Column[] {
  return order.filter((id) => !hidden.has(id)).map((id) => COLUMNS_BY_ID[id]);
}

const buildGridTemplate = buildResizableGridTemplate<ResultsColumnId, Column>;

const SELECT_COL_WIDTH = "40px";

type HeaderProps = {
  sort: SortState;
  onSort: (s: SortState) => void;
  order?: readonly ResultsColumnId[];
  hidden?: ReadonlySet<ResultsColumnId>;
  onReorder?: (next: ResultsColumnId[]) => void;
  compact?: boolean;
  trailingActions?: boolean;
  widths?: ColumnWidths;
  onResizeColumn?: (id: ResultsColumnId, px: number) => void;
  selectMode?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleAll?: () => void;
};

const EMPTY_HIDDEN: ReadonlySet<ResultsColumnId> = new Set();
const NOOP_REORDER = () => {};
const EMPTY_WIDTHS: ColumnWidths = {};

export function ResultsColumnHeaders({
  sort,
  onSort,
  order = RESULTS_COLUMN_IDS,
  hidden = EMPTY_HIDDEN,
  onReorder = NOOP_REORDER,
  compact = false,
  trailingActions = false,
  widths = EMPTY_WIDTHS,
  onResizeColumn,
  selectMode = false,
  allSelected = false,
  someSelected = false,
  onToggleAll,
}: HeaderProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function toggle(key: SortKey) {
    if (sort.key === key) {
      onSort({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
    } else {
      onSort({ key, dir: "desc" });
    }
  }
  if (compact) {
    return (
      <CompactNameHeader
        active={sort.key === "name"}
        dir={sort.dir}
        onClick={() => toggle("name")}
      />
    );
  }

  const columns = visibleColumns(order, hidden);
  const visibleIds = columns.map((c) => c.id);
  const draggableIds = visibleIds.filter((id) => !LOCKED_FIRST_SET.has(id));
  const gridTemplate =
    (selectMode ? `${SELECT_COL_WIDTH} ` : "") +
    buildGridTemplate(columns, widths) +
    (trailingActions ? ` ${ACTIONS_COL_WIDTH}` : "");

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (LOCKED_FIRST_SET.has(active.id as string)) return;
    const oldIndex = order.indexOf(active.id as ResultsColumnId);
    const rawNewIndex = order.indexOf(over.id as ResultsColumnId);
    if (oldIndex < 0 || rawNewIndex < 0) return;
    const lockedCount = order.filter((id) => LOCKED_FIRST_SET.has(id)).length;
    const newIndex = Math.max(lockedCount, rawNewIndex);
    if (oldIndex === newIndex) return;
    onReorder(arrayMove(order as ResultsColumnId[], oldIndex, newIndex));
  }

  const headerGrid = (
    <div
      className="grid h-9 items-center border-b border-grey-200 bg-grey-50 pr-3 text-tiny font-medium text-grey-900"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {selectMode && (
        <div className="flex items-center justify-center">
          <SelectCheckbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onClick={onToggleAll}
            label={allSelected ? "Deselect all" : "Select all"}
          />
        </div>
      )}
      {columns.map((column, index) => {
        const resizable = resizeHandlerFor(index, columns.length, onResizeColumn);
        return !LOCKED_FIRST_SET.has(column.id) ? (
          <SortableHeaderCell
            key={column.id}
            column={column}
            sort={sort}
            onSort={toggle}
            onResizeColumn={resizable}
          />
        ) : (
          <StaticHeaderCell
            key={column.id}
            column={column}
            sort={sort}
            onSort={toggle}
            locked={LOCKED_FIRST_SET.has(column.id)}
            onResizeColumn={resizable}
          />
        );
      })}
      {trailingActions && <div aria-hidden className="h-full" />}
    </div>
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={draggableIds} strategy={horizontalListSortingStrategy}>
        {headerGrid}
      </SortableContext>
    </DndContext>
  );
}

function SortableHeaderCell({
  column,
  sort,
  onSort,
  onResizeColumn,
}: {
  column: Column;
  sort: SortState;
  onSort: (key: SortKey) => void;
  onResizeColumn?: (id: ResultsColumnId, px: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-column-id={column.id}
      className={[
        "relative flex h-full items-center gap-2 px-2 select-none",
        column.align === "center" ? "justify-center" : "justify-start",
      ].join(" ")}
    >
      <DragHandle attributes={attributes} listeners={listeners} />
      <HeaderLabel column={column} sort={sort} onSort={onSort} />
      {onResizeColumn && <ColumnResizeHandle columnId={column.id} onResize={onResizeColumn} />}
    </div>
  );
}

function StaticHeaderCell({
  column,
  sort,
  onSort,
  locked = false,
  onResizeColumn,
}: {
  column: Column;
  sort: SortState;
  onSort: (key: SortKey) => void;
  locked?: boolean;
  onResizeColumn?: (id: ResultsColumnId, px: number) => void;
}) {
  return (
    <div
      data-column-id={column.id}
      className={[
        "relative flex h-full items-center gap-2 px-2",
        column.align === "center" ? "justify-center" : "justify-start",
      ].join(" ")}
    >
      {!locked && <span className="h-5 w-2 shrink-0" aria-hidden />}
      <HeaderLabel column={column} sort={sort} onSort={onSort} />
      {onResizeColumn && <ColumnResizeHandle columnId={column.id} onResize={onResizeColumn} />}
    </div>
  );
}

function DragHandle({
  attributes,
  listeners,
}: {
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
}) {
  return (
    <button
      type="button"
      aria-label="Drag column to reorder"
      {...attributes}
      {...listeners}
      className="flex h-5 w-2 shrink-0 cursor-grab items-center justify-center text-grey-300 hover:text-grey-900 active:cursor-grabbing touch-none"
    >
      <DragHandleIcon />
    </button>
  );
}

function DragHandleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="20"
      viewBox="0 0 8 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="2" cy="6" r="1.25" />
      <circle cx="6" cy="6" r="1.25" />
      <circle cx="2" cy="10" r="1.25" />
      <circle cx="6" cy="10" r="1.25" />
      <circle cx="2" cy="14" r="1.25" />
      <circle cx="6" cy="14" r="1.25" />
    </svg>
  );
}

function HeaderLabel({
  column,
  sort,
  onSort,
}: {
  column: Column;
  sort: SortState;
  onSort: (key: SortKey) => void;
}) {
  if (!column.sortKey) {
    return <span>{column.header}</span>;
  }
  const active = sort.key === column.sortKey;
  const iconName = !active ? "unfold_more" : sort.dir === "asc" ? "arrow_upward" : "arrow_downward";
  return (
    <button
      type="button"
      onClick={() => onSort(column.sortKey!)}
      aria-label={
        active
          ? `${column.header}, sorted ${sort.dir === "asc" ? "ascending" : "descending"}`
          : `Sort by ${column.header}`
      }
      className={`inline-flex items-center gap-1 transition-colors ${
        active ? "text-black" : "hover:text-black"
      }`}
    >
      <span>{column.header}</span>
      <Icon name={iconName} size={20} />
    </button>
  );
}

function CompactNameHeader({
  active,
  dir,
  onClick,
}: {
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  const iconName = !active ? "unfold_more" : dir === "asc" ? "arrow_upward" : "arrow_downward";
  return (
    <div
      className="flex h-9 items-center border-b border-grey-200 bg-grey-50 px-3 text-tiny font-medium text-grey-900"
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 transition-colors ${
          active ? "text-black" : "hover:text-black"
        }`}
      >
        <span>Name</span>
        <Icon name={iconName} size={20} />
      </button>
    </div>
  );
}

type ResultsTableProps = {
  matches: Match[];
  sort: SortState;
  order?: readonly ResultsColumnId[];
  hidden?: ReadonlySet<ResultsColumnId>;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  compact?: boolean;
  rowActions?: (match: Match) => RowAction[];
  page?: number;
  rowsPerPage?: number;
  widths?: ColumnWidths;
  selectMode?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
};

export function ResultsTable({
  matches,
  sort,
  order = RESULTS_COLUMN_IDS,
  hidden = EMPTY_HIDDEN,
  onSelect,
  selectedId,
  compact = false,
  rowActions,
  page,
  rowsPerPage,
  widths = EMPTY_WIDTHS,
  selectMode = false,
  selectedIds,
  onToggleSelect,
}: ResultsTableProps) {
  const sorted = useMemo(() => sortMatches(matches, sort.key, sort.dir), [matches, sort]);
  const visible =
    page && rowsPerPage ? sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage) : sorted;

  const columns = compact ? [COLUMNS_BY_ID.name] : visibleColumns(order, hidden);
  const withActions = !compact && Boolean(rowActions);
  const gridTemplate =
    (selectMode ? `${SELECT_COL_WIDTH} ` : "") +
    buildGridTemplate(columns, widths) +
    (withActions ? ` ${ACTIONS_COL_WIDTH}` : "");

  return (
    <div className="flex flex-col bg-white">
      {visible.map((m) => (
        <Row
          key={m.id}
          match={m}
          columns={columns}
          gridTemplate={gridTemplate}
          onSelect={onSelect}
          selected={selectedId === m.id}
          compact={compact}
          rowActions={withActions ? rowActions : undefined}
          selectMode={selectMode}
          checked={selectedIds?.has(m.id) ?? false}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
}

function Row({
  match,
  columns,
  gridTemplate,
  onSelect,
  selected = false,
  compact = false,
  rowActions,
  selectMode = false,
  checked = false,
  onToggleSelect,
}: {
  match: Match;
  columns: Column[];
  gridTemplate: string;
  onSelect?: (id: string) => void;
  selected?: boolean;
  compact?: boolean;
  rowActions?: (match: Match) => RowAction[];
  selectMode?: boolean;
  checked?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const rowClick = selectMode ? () => onToggleSelect?.(match.id) : () => onSelect?.(match.id);
  const interactive = selectMode ? Boolean(onToggleSelect) : Boolean(onSelect);
  const baseClasses = [
    "h-12 items-center border-b border-l-4 border-b-grey-100 pr-3 text-small text-black hover:bg-grey-50",
    selectMode
      ? checked
        ? "border-l-transparent bg-green-500-12 hover:bg-green-500-12"
        : "border-l-transparent"
      : selected
        ? "border-l-green-400 bg-grey-50"
        : "border-l-transparent",
    interactive ? "cursor-pointer" : "",
  ];
  const handlers = {
    role: interactive ? ("button" as const) : undefined,
    tabIndex: interactive ? 0 : undefined,
    onClick: interactive ? rowClick : undefined,
    onKeyDown: interactive
      ? (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            rowClick();
          }
        }
      : undefined,
    "aria-current": !selectMode && selected ? ("true" as const) : undefined,
  };

  if (compact) {
    return (
      <div {...handlers} className={["flex", ...baseClasses].join(" ")}>
        <div className="truncate px-3">{match.name}</div>
      </div>
    );
  }

  return (
    <div
      {...handlers}
      style={{ gridTemplateColumns: gridTemplate }}
      className={["grid", ...baseClasses].join(" ")}
    >
      {selectMode && (
        <div className="flex items-center justify-center">
          <SelectCheckbox
            checked={checked}
            label={checked ? `Deselect ${match.name}` : `Select ${match.name}`}
          />
        </div>
      )}
      {columns.map((column) => (
        <div key={column.id} className="min-w-0">
          {column.cell(match)}
        </div>
      ))}
      {rowActions && (
        <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu actions={rowActions(match)} label={`Actions for ${match.name}`} />
        </div>
      )}
    </div>
  );
}

function SourceChip({ source }: { source: string }) {
  const isAdvMedia = source === "ADV-MEDIA";
  return (
    <span
      title={source}
      className={[
        "inline-flex max-w-full items-center rounded-[4px] px-2 py-1 text-tiny font-medium",
        isAdvMedia ? "text-white" : "bg-grey-100 text-grey-900",
      ].join(" ")}
      style={isAdvMedia ? { backgroundColor: "#f6b733" } : undefined}
    >
      <span className="min-w-0 truncate">{source}</span>
    </span>
  );
}

export function sortMatches(matches: Match[], key: SortKey, dir: SortDir): Match[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...matches].sort((a, b) => {
    let va: string | number = "";
    let vb: string | number = "";
    if (key === "score") {
      va = a.score;
      vb = b.score;
    } else if (key === "name") {
      va = a.name;
      vb = b.name;
    } else if (key === "country") {
      va = a.country;
      vb = b.country;
    } else if (key === "dob") {
      va = a.dob ?? "";
      vb = b.dob ?? "";
    } else if (key === "type") {
      va = a.type;
      vb = b.type;
    } else if (key === "source") {
      va = a.source;
      vb = b.source;
    } else if (key === "firstImported") {
      va = a.firstImported ?? "";
      vb = b.firstImported ?? "";
    } else if (key === "lastUpdated") {
      va = a.lastUpdated;
      vb = b.lastUpdated;
    } else if (key === "status") {
      va = a.decision?.status ?? DEFAULT_DECISION_STATUS;
      vb = b.decision?.status ?? DEFAULT_DECISION_STATUS;
    } else if (key === "mediaSource") {
      va = a.mediaSource ?? "";
      vb = b.mediaSource ?? "";
    }
    if (va === vb) return 0;
    return va < vb ? -1 * sign : 1 * sign;
  });
}
